import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Input, InputWrapper, Label, ErrorMessage } from '../components/Input';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

// ─── Layout ─────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xl};
  padding-top: 8vh;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.md};
    padding-top: 4vh;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// ─── Title ──────────────────────────────────────────────────────────────────

const BrandTitle = styled.h1`
  position: fixed;
  top: ${({ theme }) => theme.spacing.lg};
  left: ${({ theme }) => theme.spacing.xl};
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  letter-spacing: 0.15em;
  background: linear-gradient(135deg, #3B82F6, #8B5CF6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  z-index: 10;
  animation: ${popIn} 0.4s ease-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 16px;
    top: ${({ theme }) => theme.spacing.md};
    left: ${({ theme }) => theme.spacing.md};
  }
`;

// ─── Avatar ─────────────────────────────────────────────────────────────────

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  animation: ${popIn} 0.5s ease-out;
`;

const AvatarImg = styled.img`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.2);
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const AvatarName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// ─── Chat ───────────────────────────────────────────────────────────────────

const ChatArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const MsgRow = styled.div<{ $from: 'novah' | 'user' }>`
  display: flex;
  justify-content: ${({ $from }) => ($from === 'user' ? 'flex-end' : 'flex-start')};
  animation: ${fadeIn} 0.3s ease-out;
`;

const Bubble = styled.div<{ $from: 'novah' | 'user' }>`
  max-width: 88%;
  padding: 10px 14px;
  border-radius: ${({ $from }) => ($from === 'novah' ? '4px 16px 16px 16px' : '16px 4px 16px 16px')};
  font-size: 14px;
  line-height: 1.55;
  background: ${({ $from, theme }) => ($from === 'novah' ? theme.colors.surface : theme.colors.primary)};
  color: ${({ $from, theme }) => ($from === 'novah' ? theme.colors.text : '#fff')};
  border: ${({ $from, theme }) => ($from === 'novah' ? theme.borders.thin : 'none')};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 6px 4px;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.textMuted};
    animation: ${dotBounce} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

// ─── Actions ────────────────────────────────────────────────────────────────

const Actions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  animation: ${fadeIn} 0.4s ease-out;
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};

  > * { flex: 1; }
`;

// ─── Forms ──────────────────────────────────────────────────────────────────

const FormWrap = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeIn} 0.4s ease-out;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SwitchText = styled.p`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SwitchLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const FooterWrap = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.xxl};
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FooterLine = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const FooterAccent = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// ─── Types ──────────────────────────────────────────────────────────────────

interface ChatMsg {
  id: number;
  from: 'novah' | 'user';
  text: string;
}

type Step = 'intro' | 'choice' | 'login' | 'register';

// ─── Component ──────────────────────────────────────────────────────────────

export function LandingPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState<Step>('intro');
  const msgIdRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nextId = () => ++msgIdRef.current;

  const scroll = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 80);
  }, []);

  const addNovah = useCallback((text: string): Promise<void> => {
    return new Promise((resolve) => {
      setTyping(true);
      scroll();
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [...prev, { id: nextId(), from: 'novah', text }]);
        scroll();
        resolve();
      }, 600);
    });
  }, [scroll]);

  const addUser = useCallback((text: string) => {
    setMessages((prev) => [...prev, { id: nextId(), from: 'user', text }]);
    scroll();
  }, [scroll]);

  // Intro sequence
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const run = async () => {
      await new Promise((r) => setTimeout(r, 400));
      await addNovah("Hey! I'm Novah, your AI content assistant.");
      await new Promise((r) => setTimeout(r, 300));
      await addNovah("I help brands turn their website into weeks of social media content — automatically.");
      await new Promise((r) => setTimeout(r, 300));
      await addNovah("Ready to get started?");
      setStep('choice');
    };
    run();
  }, [addNovah]);

  // Handlers
  const chooseLogin = async () => {
    addUser('I have an account');
    await addNovah("Welcome back! Sign in below.");
    setStep('login');
  };

  const chooseRegister = async () => {
    addUser("I'm new here");
    await addNovah("Let's get you set up — it only takes a few seconds.");
    setStep('register');
  };

  const switchToLogin = async () => {
    setFormError('');
    addUser('I already have an account');
    await addNovah("No problem — sign in below.");
    setStep('login');
  };

  const switchToRegister = async () => {
    setFormError('');
    addUser('I want to create an account');
    await addNovah("Let's get you started!");
    setStep('register');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (password !== confirmPassword) { setFormError('Passwords do not match'); return; }
    if (password.length < 8) { setFormError('Password must be at least 8 characters'); return; }
    setIsSubmitting(true);
    try {
      await register(email, password, name);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page>
      <Container>
        <BrandTitle>AgencySMM</BrandTitle>
        <AvatarSection>
          <AvatarImg src="/novah_laugh.png" alt="Novah" />
          <AvatarName>Novah</AvatarName>
        </AvatarSection>

        <ChatArea>
          {messages.map((msg) => (
            <MsgRow key={msg.id} $from={msg.from}>
              <Bubble $from={msg.from}>{msg.text}</Bubble>
            </MsgRow>
          ))}

          {typing && (
            <MsgRow $from="novah">
              <Bubble $from="novah">
                <TypingDots><span /><span /><span /></TypingDots>
              </Bubble>
            </MsgRow>
          )}
        </ChatArea>

        {step === 'choice' && (
          <Actions>
            <ActionRow>
              <Button onClick={chooseLogin} $variant="outline">
                I have an account
              </Button>
              <Button onClick={chooseRegister}>
                I&apos;m new here
              </Button>
            </ActionRow>
            <Button $variant="ghost" $size="sm" onClick={() => navigate('/demo')}>
              or try a free demo
            </Button>
          </Actions>
        )}

        {step === 'login' && (
          <FormWrap>
            <Form onSubmit={handleLogin}>
              <InputWrapper>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required autoFocus
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password" required
                />
              </InputWrapper>
              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              <Button type="submit" $fullWidth disabled={isSubmitting}>
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </Form>
            <SwitchText>
              Don&apos;t have an account?{' '}
              <SwitchLink onClick={switchToRegister}>Sign up</SwitchLink>
            </SwitchText>
          </FormWrap>
        )}

        {step === 'register' && (
          <FormWrap>
            <Form onSubmit={handleRegister}>
              <InputWrapper>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name" type="text" value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name" required autoFocus
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="reg-email">Email</Label>
                <Input
                  id="reg-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="reg-password">Password</Label>
                <Input
                  id="reg-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters" required
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="reg-confirm">Confirm Password</Label>
                <Input
                  id="reg-confirm" type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password" required
                />
              </InputWrapper>
              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              <Button type="submit" $fullWidth disabled={isSubmitting}>
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </Button>
            </Form>
            <SwitchText>
              Already have an account?{' '}
              <SwitchLink onClick={switchToLogin}>Sign in</SwitchLink>
            </SwitchText>
          </FormWrap>
        )}

        <div ref={scrollRef} />

        <FooterWrap>
          <FooterLine>Created with love by <FooterAccent>Charly</FooterAccent></FooterLine>
          <FooterLine>Powered by <FooterAccent>CoopenStudio</FooterAccent></FooterLine>
        </FooterWrap>
      </Container>
    </Page>
  );
}
